import * as _ from 'lodash';
import { useMemo } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
  typeguards as commonTypeguards,
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types/';

import {
  AttributeValueFragmentFragmentGQLType,
  SuccessFinancialCovenantGQLType,
  SuccessPortfolioCovenantGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import {
  FacilitiesTableRowData,
  FacilitiesTableTotalRowData,
} from '../facilitiesTableOptions';
import { useGetBalanceSheetExposureDataQuery } from '../getBalanceSheetExposureData.gql.generated';
import {
  GetFacilitiesTableDataQueryGQLType,
  useGetFacilitiesTableDataQuery,
} from '../getFacilitiesTableData.gql.generated';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { CreditFacility } from '~/contexts/CreditFacilitiesProvider/useCreditFacilities';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { getDefaultDashboardDisplayStartDate } from '~/packages/common-frontend/constants';
import { useCheckIfAnyFacilitiesHaveBorrowingBaseQuery } from '~/packages/credit-facility/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { parseInterestRateDisplayForCapitalProvider } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/utils';

/**
 * Given an array of all portcov results for multiple credit facilities
 * Returns the latest covenant evaluation results for each covenantKey if it exists in the latest credit agreement
 */
const getLatestCovenantResultsForCreditFacility = ({
  allCovenantResults,
  latestCreditAgreement,
  covenantType,
}: {
  allCovenantResults: (
    | SuccessFinancialCovenantGQLType
    | SuccessPortfolioCovenantGQLType
  )[];
  latestCreditAgreement:
    | CreditAgreement.DomainEntity.CreditAgreementFullArtifact
    | CreditAgreement.DomainEntity.CreditAgreementDocument;
  covenantType: 'financial' | 'portfolio';
}) => {
  const creditFacilityId = latestCreditAgreement.creditFacilityId;

  const allCurrentCovenantKeysFromCreditAgreement: Set<string> =
    covenantType === 'portfolio'
      ? new Set(
          latestCreditAgreement.portfolioCovenants.map(
            (portCov) => portCov.covenantKey,
          ),
        )
      : new Set(
          latestCreditAgreement.financialCovenants.map(
            (finCov) => finCov.covenantKey,
          ),
        );

  const latestVersionCovenantResultsForCreditFacility = allCovenantResults.reduce(
    (acc, covenantResult) => {
      const covenantKey = covenantResult.covenantKey;

      // filter first by creditFacilityId and covenants that exist in the latest credit agreement
      if (
        covenantResult.creditFacilityId === creditFacilityId &&
        allCurrentCovenantKeysFromCreditAgreement.has(covenantKey)
      ) {
        // if the covenantKey is not yet in the acc,
        // or if the current result has a newer reportingDate,
        // or the same reportingDate but a higher version,
        // update the acc
        if (
          !acc[covenantKey] ||
          covenantResult.reportingDate > acc[covenantKey].reportingDate ||
          (covenantResult.reportingDate === acc[covenantKey].reportingDate &&
            covenantResult.version > acc[covenantKey].version)
        ) {
          // eslint-disable-next-line no-param-reassign
          acc[covenantKey] = covenantResult;
        }
      }
      return acc;
    },
    {} as Record<
      string,
      SuccessPortfolioCovenantGQLType | SuccessFinancialCovenantGQLType
    >,
  );

  return Object.values(latestVersionCovenantResultsForCreditFacility);
};

export const computeTotalsRow = (
  creditFacilityRows: FacilitiesTableRowData[],
  attributeValues: AttributeValueFragmentFragmentGQLType[],
): FacilitiesTableTotalRowData => {
  const addNumbers = (...args: number[]): number => {
    const total = args.reduce((acc, curr) => acc + curr, 0);
    return commonUtils.jsRound({ value: total, precision: 2 });
  };

  const stripedCreditFacilityRows = creditFacilityRows.map((row) =>
    _.omit(row, [
      'maturityDate',
      'closingDate',
      'dateOfLastFundingRequest',
      'receivableAdvanceRate',
      'unusedFees',
      'upfrontFees',
      'interestRates',
    ]),
  );

  const totalsRow = stripedCreditFacilityRows.reduce(
    (acc, row) => ({
      ...acc,
      globalCommitmentAmount: addNumbers(
        acc.globalCommitmentAmount,
        row.globalCommitmentAmount,
      ),
      globalLoanOutstanding: addNumbers(
        acc.globalLoanOutstanding,
        row.globalLoanOutstanding,
      ),
      globalUnusedCommitment: addNumbers(
        acc.globalUnusedCommitment,
        row.globalUnusedCommitment,
      ),
      capitalProviderCommitmentAmount: addNumbers(
        acc.capitalProviderCommitmentAmount,
        row.capitalProviderCommitmentAmount,
      ),
      capitalProviderLoanOutstanding: addNumbers(
        acc.capitalProviderLoanOutstanding,
        row.capitalProviderLoanOutstanding,
      ),
      capitalProviderUnused: addNumbers(
        acc.capitalProviderUnused,
        row.capitalProviderUnused,
      ),
      collectionsAccountBalance: addNumbers(
        acc.collectionsAccountBalance,
        row.collectionsAccountBalance,
      ),
      requiredReserve: addNumbers(acc.requiredReserve, row.requiredReserve),
      totalBorrowingBase: addNumbers(
        acc.totalBorrowingBase,
        row.totalBorrowingBase,
      ),
      borrowingBaseFromReceivables: addNumbers(
        acc.borrowingBaseFromReceivables,
        row.borrowingBaseFromReceivables,
      ),
      borrowingBaseCashContribution: addNumbers(
        acc.borrowingBaseCashContribution,
        row.borrowingBaseCashContribution,
      ),
      borrowingBaseAvailability: addNumbers(
        acc.borrowingBaseAvailability,
        row.borrowingBaseAvailability,
      ),
      accruedInterestAndFees: addNumbers(
        acc.accruedInterestAndFees,
        row.accruedInterestAndFees,
      ),
      financialCovenantsFailed: !_.isNil(row.financialCovenantsFailed)
        ? addNumbers(
            acc.financialCovenantsFailed ?? 0,
            row.financialCovenantsFailed,
          )
        : acc.financialCovenantsFailed,
      portfolioCovenantsFailed: !_.isNil(row.portfolioCovenantsFailed)
        ? addNumbers(
            acc.portfolioCovenantsFailed ?? 0,
            row.portfolioCovenantsFailed,
          )
        : acc.portfolioCovenantsFailed,
    }),
    {
      creditFacilityId: '',
      creditFacilityName: 'Portfolio totals',
      externalFacilityId: null,
      globalCommitmentAmount: 0,
      globalLoanOutstanding: 0,
      globalUnusedCommitment: 0,
      globalUtilization: 0,
      capitalProviderCommitmentAmount: 0,
      capitalProviderLoanOutstanding: 0,
      capitalProviderUnused: 0,
      capitalProviderUtilization: 0,
      collectionsAccountBalance: 0,
      requiredReserve: 0,
      totalBorrowingBase: 0,
      borrowingBaseFromReceivables: 0,
      borrowingBaseCashContribution: 0,
      borrowingBaseAvailability: 0,
      accruedInterestAndFees: 0,
      financialCovenantsFailed: null,
      portfolioCovenantsFailed: null,
    },
  );

  // Utilization based on totals
  totalsRow.globalUtilization =
    totalsRow.globalCommitmentAmount > 0
      ? totalsRow.globalLoanOutstanding / totalsRow.globalCommitmentAmount
      : 0;
  totalsRow.capitalProviderUtilization =
    totalsRow.capitalProviderCommitmentAmount > 0
      ? totalsRow.capitalProviderLoanOutstanding /
        totalsRow.capitalProviderCommitmentAmount
      : 0;

  const emptyAttributes = Array.from(
    new Set(attributeValues.map((av) => av.name)),
  ).reduce<{ [key: string]: null }>((acc, val) => {
    return { ...acc, [val]: null };
  }, {});

  return {
    ...totalsRow,
    ...emptyAttributes,
    maturityDate: null,
    closingDate: null,
    receivableAdvanceRate: null,
    dateOfLastFundingRequest: undefined,
    unusedFees: '-',
    upfrontFees: '-',
    interestRates: '-',
  };
};

const getAttributeValue = (av: AttributeValueFragmentFragmentGQLType) => {
  switch (av.__typename) {
    case 'BooleanAttributeValue':
      return av.booleanValue;
    case 'FloatAttributeValue':
      return av.floatValue;
    case 'IntegerAttributeValue':
      return av.integerValue;
    case 'PercentageRateAttributeValue':
      return av.percentageRateValue;
    case 'StringAttributeValue':
      return av.stringValue;
    case 'FullDateStrAttributeValue':
      return av.fullDateStrValue;
    case 'StringArrayAttributeValue':
      return av.stringArrayValue;
    default:
      return finleyAssertUnreachable(av);
  }
};
export type FacilitiesTableRowDataWithAttributes = FacilitiesTableRowData & {
  [key: string]: string | number | boolean | null;
};

const mapQueryDataToTableRows = ({
  organizationId,
  creditFacilities,
  facilitiesTableQueryData: {
    organization: { currentCreditAgreementArtifacts },
    fundingRequests,
    expensesFeesLedgerForAllCountersigners,
    outstandingLedgerForAllCountersigners,
    outstandingLedgerForCapitalProvider,
    latestBorrowingBaseReports,
    getSuccessPortfolioCovenantsByCreditFacilityIds,
    getSuccessFinancialCovenantsByCreditFacilityIds,
    getAttributeValuesByParams,
  },
}: {
  organizationId: string;
  creditFacilities: CreditFacility[];
  facilitiesTableQueryData: GetFacilitiesTableDataQueryGQLType;
}): FacilitiesTableRowDataWithAttributes[] => {
  const creditAgreements = currentCreditAgreementArtifacts.map(
    (creditAgreementFullArtifact) => {
      const parsedCreditAgreement = parseCreditAgreementFullArtifact(
        creditAgreementFullArtifact.creditAgreement,
      );
      if (!parsedCreditAgreement) {
        throw Error(
          `Expect creditAgreementFullArtifact to exist for creditAgreementId ${creditAgreementFullArtifact.id}`,
        );
      }
      return parsedCreditAgreement;
    },
  );

  // Bucket data from all of the different queries by credit facility
  const creditFacilitiesWithData = creditFacilities.map((creditFacility) => {
    const lastCreditAgreement = creditAgreements.find(
      (obj) => obj.creditFacilityId === creditFacility.id,
    );
    if (!lastCreditAgreement) {
      throw Error('Missing "lastCreditAgreement" in facilities table');
    }

    const latestPortfolioCovenantResultsForCreditFacility = getLatestCovenantResultsForCreditFacility(
      {
        allCovenantResults: getSuccessPortfolioCovenantsByCreditFacilityIds,
        latestCreditAgreement: lastCreditAgreement,
        covenantType: 'portfolio',
      },
    );

    const latestFinancialCovenantResultsForCreditFacility = getLatestCovenantResultsForCreditFacility(
      {
        allCovenantResults: getSuccessFinancialCovenantsByCreditFacilityIds,
        latestCreditAgreement: lastCreditAgreement,
        covenantType: 'financial',
      },
    );

    return {
      creditFacility,
      lastCreditAgreement,
      lastFundingRequest: _.orderBy(
        fundingRequests.nodes.filter(
          (obj) => obj.creditFacilityId === creditFacility.id,
        ),
        'created_at',
        'desc',
      )[0] as
        | GetFacilitiesTableDataQueryGQLType['fundingRequests']['nodes'][number]
        | undefined,
      expensesFeesLedgerForFacilityForAllCountersigners: expensesFeesLedgerForAllCountersigners.find(
        (obj) => obj.creditFacilityId === creditFacility.id,
      ),
      outstandingLedgerForFacilityForAllCountersigners: outstandingLedgerForAllCountersigners.find(
        (obj) => obj.creditFacilityId === creditFacility.id,
      ),
      outstandingLedgerForFacilityForCapitalProvider: outstandingLedgerForCapitalProvider.find(
        (obj) => obj.creditFacilityId === creditFacility.id,
      ),
      portfolioCovenantsForFacility: latestPortfolioCovenantResultsForCreditFacility,
      financialCovenantsForFacility: latestFinancialCovenantResultsForCreditFacility,
      borrowingBaseReport: latestBorrowingBaseReports.find(
        (obj) => obj?.creditFacilityId === creditFacility.id,
      ),
    };
  });

  // Map credit facility data to table row derived values
  const emptyAttributes = Array.from(
    new Set(getAttributeValuesByParams.map((av) => av.name)),
  ).reduce<{ [key: string]: null }>((acc, val) => {
    return { ...acc, [val]: null };
  }, {});

  const cfAttributeMap = getAttributeValuesByParams.reduce<{
    [cfId: string]: {
      [name: string]: string | number | boolean | null | string[];
    };
  }>((acc, val) => {
    if (!acc[val.entityIdentifier]) {
      // eslint-disable-next-line no-param-reassign
      acc[val.entityIdentifier] = {};
    }
    // eslint-disable-next-line no-param-reassign
    acc[val.entityIdentifier][val.name] = getAttributeValue(val) ?? null;
    return acc;
  }, {});

  const creditFacilityRows = creditFacilitiesWithData.map(
    ({
      creditFacility,
      lastCreditAgreement,
      lastFundingRequest,
      expensesFeesLedgerForFacilityForAllCountersigners,
      outstandingLedgerForFacilityForAllCountersigners,
      outstandingLedgerForFacilityForCapitalProvider,
      portfolioCovenantsForFacility,
      financialCovenantsForFacility,
      borrowingBaseReport,
    }): FacilitiesTableRowData => {
      const isCapitalProvider = lastCreditAgreement.facilityMechanics.parties.capitalProviders.some(
        (capitalProvider) => capitalProvider.entity.id === organizationId,
      );
      const globalCommitmentAmount = _.sumBy(
        lastCreditAgreement.facilityMechanics.parties.capitalProviders,
        (capitalProviderParty) =>
          capitalProviderParty.commitmentAmount.value ?? 0,
      );
      const globalLoanOutstanding =
        outstandingLedgerForFacilityForAllCountersigners?.ledgerBalance ?? 0;

      const attributeMap = {
        ...emptyAttributes,
        ...(cfAttributeMap[creditFacility.id] ?? {}),
      };

      const borrowingBaseFromReceivables =
        borrowingBaseReport?.maxReceivableAdvanceAmount ?? 0;
      const borrowingBaseCashContribution =
        borrowingBaseReport?.totalCashBorrowingBaseAmount ?? 0;
      const totalBorrowingBase = commonUtils.jsRound({
        value: borrowingBaseFromReceivables + borrowingBaseCashContribution,
        precision: 2,
      });
      const maximumAvailableAmount = Math.min(
        totalBorrowingBase,
        borrowingBaseReport?.commitmentAmount ?? 0,
      );
      const borrowingBaseAvailability = commonUtils.jsRound({
        value:
          maximumAvailableAmount -
          (borrowingBaseReport?.currentPrincipalBalance ?? 0),
        precision: 2,
      });

      // This receivable advance rate is a coerced number version of the formula in the CA
      // Most facilities should have a fixed number value, otherwise just show empty for simplicity
      let receivableAdvanceRate: string | null = null;
      if (
        lastCreditAgreement.borrowingBaseMechanics.calcAgentMechanics
          .loanProductMechanics.length === 1
      ) {
        const loanProductMechanics =
          lastCreditAgreement.borrowingBaseMechanics.calcAgentMechanics
            .loanProductMechanics[0];

        const textValue = loanProductMechanics.receivableAdvanceRate.value;
        // Check for both presence and placeholder
        // Unclear if all committed CAs adhere to type def correctly at runtime for presence of value
        if (textValue && textValue !== 'Placeholder') {
          receivableAdvanceRate =
            loanProductMechanics.receivableAdvanceRate.value;
        }
      }

      finleyAssert(
        commonTypeguards.isFullDateStr(
          lastCreditAgreement.facilityMechanics.finalMaturityDate.value,
        ),
        `Expected lastCreditAgreement.facilityMechanics.finalMaturityDate.value to be FullDateStr, but got ${lastCreditAgreement.facilityMechanics.finalMaturityDate.value}`,
      );
      finleyAssert(
        commonTypeguards.isFullDateStr(
          lastCreditAgreement.facilityMechanics.closingDate.value,
        ),
        `Expected lastCreditAgreement.facilityMechanics.closingDate.value to be FullDateStr, but got ${lastCreditAgreement.facilityMechanics.closingDate.value}`,
      );

      const revolvingPeriodEndDateRaw =
        lastCreditAgreement.facilityMechanics.revolvingCommitmentTermination
          ?.value;

      let revolvingPeriodEndDate: Util.FullDateStr | undefined;
      if (revolvingPeriodEndDateRaw) {
        finleyAssert(
          commonTypeguards.isFullDateStr(revolvingPeriodEndDateRaw),
          `Expected lastCreditAgreement.facilityMechanics.revolvingCommitmentTermination to be FullDateStr, but got ${revolvingPeriodEndDateRaw}`,
        );
        revolvingPeriodEndDate = revolvingPeriodEndDateRaw;
      }

      return {
        // "Facility"
        creditFacilityId: creditFacility.id,
        creditFacilityName: creditFacility.name,
        externalFacilityId:
          lastCreditAgreement.facilityMechanics.externalFacilityId?.value ??
          null,
        maturityDate:
          lastCreditAgreement.facilityMechanics.finalMaturityDate.value,
        closingDate: lastCreditAgreement.facilityMechanics.closingDate.value,
        revolvingPeriodEndDate,

        // "Global Commitment Amount"
        globalCommitmentAmount: globalCommitmentAmount ?? 0,
        // "Global Outstanding Balance"
        globalLoanOutstanding,
        // "Global Unused Commitment"
        globalUnusedCommitment:
          globalCommitmentAmount -
          (outstandingLedgerForFacilityForAllCountersigners?.ledgerBalance ??
            0),
        // "Global Utilization"
        globalUtilization:
          globalCommitmentAmount > 0
            ? globalLoanOutstanding / globalCommitmentAmount
            : 0,

        // Capital provider-only cells. These are hidden for borrowers.
        ...(() => {
          const capitalProviderCommitmentAmount =
            lastCreditAgreement.facilityMechanics.parties.capitalProviders.find(
              (cp) => cp.entity.id === organizationId,
            )?.commitmentAmount.value ?? 0;
          const capitalProviderLoanOutstanding =
            outstandingLedgerForFacilityForCapitalProvider?.ledgerBalance ?? 0;
          const capitalProviderUnused =
            capitalProviderCommitmentAmount - capitalProviderLoanOutstanding;

          return {
            // "Capital Provider Commitment Amount"
            capitalProviderCommitmentAmount,
            // "Capital Provider Outstanding Balance"
            capitalProviderLoanOutstanding,
            // "Capital Provider Unused Commitment"
            capitalProviderUnused,
            // "Capital Provider Utilization"
            capitalProviderUtilization:
              capitalProviderCommitmentAmount > 0
                ? capitalProviderLoanOutstanding /
                  capitalProviderCommitmentAmount
                : 0,
          };
        })(),

        // "Collections Account Balance"
        collectionsAccountBalance:
          borrowingBaseReport?.totalCollectionsAccountCash ?? 0,
        // "Required reserve"
        requiredReserve: borrowingBaseReport?.borrowingBaseReserveAmount ?? 0,
        // "Borrowing Base Availability"
        borrowingBaseAvailability,
        // "Total Borrowing Base"
        // !TODO @danitian Remove derivation during borrowing base migration phase 3
        totalBorrowingBase,
        // "Borrowing Base from Receivables"
        borrowingBaseFromReceivables,
        // "Receivable Advance Rate" (from CA configuration)
        receivableAdvanceRate,
        // "Borrowing Base from Cash"
        borrowingBaseCashContribution,
        // "Accrued Interest and Fees"
        accruedInterestAndFees:
          expensesFeesLedgerForFacilityForAllCountersigners?.ledgerBalance ?? 0,
        // "Interest Rates"
        interestRates: lastCreditAgreement.facilityMechanics.parties.capitalProviders
          .filter(
            (capitalProvider) =>
              !isCapitalProvider ||
              capitalProvider.entity.id === organizationId,
          )
          .map((capitalProvider) => {
            const rate = parseInterestRateDisplayForCapitalProvider(
              capitalProvider,
            );
            return `${capitalProvider.entity.name}: ${rate}`;
          })
          .join(', '),
        unusedFees: lastCreditAgreement.facilityMechanics.parties.capitalProviders.some(
          (capitalProvider) => capitalProvider.unusedFee,
        )
          ? lastCreditAgreement.facilityMechanics.parties.capitalProviders
              .filter(
                (capitalProvider) =>
                  !isCapitalProvider ||
                  capitalProvider.entity.id === organizationId,
              )
              .map((capitalProvider) => {
                const unusedFee = capitalProvider.unusedFee;
                if (unusedFee) {
                  const formattedRate = commonUtils.numberToFormattedString(
                    unusedFee.value,
                    'percentage',
                    false,
                  );
                  return `${capitalProvider.entity.name}: ${formattedRate}`;
                }

                return `${capitalProvider.entity.name}: n/a`;
              })
              .join(', ')
          : '-',
        upfrontFees: lastCreditAgreement.facilityMechanics.parties.capitalProviders.some(
          (capitalProvider) => capitalProvider.upFrontFee,
        )
          ? lastCreditAgreement.facilityMechanics.parties.capitalProviders
              .filter(
                (capitalProvider) =>
                  !isCapitalProvider ||
                  capitalProvider.entity.id === organizationId,
              )
              .map((capitalProvider) => {
                const upfrontFee = capitalProvider.upFrontFee;
                if (upfrontFee) {
                  const formattedRate = commonUtils.numberToFormattedString(
                    upfrontFee.value,
                    'percentage',
                    false,
                  );
                  return `${capitalProvider.entity.name}: ${formattedRate}`;
                }

                return `${capitalProvider.entity.name}: n/a`;
              })
              .join(', ')
          : '-',
        // "Date of Last Funding Request"
        dateOfLastFundingRequest: lastFundingRequest
          ? commonDate.epochMsToFullDateStr(lastFundingRequest.created_at)
          : undefined,
        // "Portfolio Covenants"
        // null value will be rendered as '-'
        portfolioCovenantsFailed: portfolioCovenantsForFacility.length
          ? _.sumBy(
              portfolioCovenantsForFacility,
              (portfolioCovenantResult) => {
                if (portfolioCovenantResult.result !== 'PASS') {
                  return 1;
                }
                return 0;
              },
            )
          : null,
        // "Financial Covenants"
        // null value will be rendered as '-'
        financialCovenantsFailed: financialCovenantsForFacility.length
          ? _.sumBy(
              financialCovenantsForFacility,
              (financialCovenantResult) => {
                if (financialCovenantResult.result !== 'PASS') {
                  return 1;
                }
                return 0;
              },
            )
          : null,

        ...attributeMap,
      };
    },
  );
  return creditFacilityRows;
};

// Exports
export const useFacilitiesTableData = () => {
  const flags = useFeatureFlags();
  const { user } = useUserAuthOrThrow();
  const organizationId = user.organizationId;

  // Get credit facilities for this org
  const { facilities: creditFacilities } = useCreditFacilitiesOrThrow();
  const creditFacilityIds = creditFacilities.map(({ id }) => id);

  // Get the credit facility details that we can query in aggregate
  // TODO @sachinr4 - look into consolidating accounting calculations and aggregations used in this query (eg. outstanding balance)
  const facilitiesTableQuery = useGetFacilitiesTableDataQuery({
    // No credit facility IDs, most likely waiting on the credit facility query
    skip: creditFacilityIds.length === 0 || flags.isSlsAgentLender,
    fetchPolicy: 'cache-and-network',
    variables: {
      creditFacilityIds,
      creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
      organizationId,
      startDate: commonDate.fullDateStrToEpoch(
        getDefaultDashboardDisplayStartDate(),
      ),
      startDateStr: getDefaultDashboardDisplayStartDate(),
      getAttributeValuesParams: {
        organizationId,
        entities: [
          {
            entityDomain: 'creditFacility',
            entityIdentifiers: creditFacilities.map((cf) => cf.id),
          },
        ],
      },
    },
  });

  const checkHasBorrowingBaseQuery = useCheckIfAnyFacilitiesHaveBorrowingBaseQuery(
    {
      skip: creditFacilityIds.length === 0 || flags.isSlsAgentLender,
      variables: {
        creditFacilityIds,
        creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
      },
    },
  );
  const hasBorrowingBase =
    checkHasBorrowingBaseQuery.data?.hasBorrowingBase ?? false;

  const { computedRows, totalsRow, av: attributeValues } = useMemo(() => {
    if (flags.isSlsAgentLender)
      return {
        computedRows: [],
        totalsRow: computeTotalsRow([], []),
        av: [],
      };
    const computed = facilitiesTableQuery.data
      ? mapQueryDataToTableRows({
          creditFacilities,
          facilitiesTableQueryData: facilitiesTableQuery.data,
          organizationId,
        })
      : [];

    const av = facilitiesTableQuery.data
      ? facilitiesTableQuery.data.getAttributeValuesByParams
      : [];

    return {
      av,
      computedRows: computed,
      totalsRow: computeTotalsRow(computed, av),
    };
  }, [
    creditFacilities,
    facilitiesTableQuery.data,
    flags.isSlsAgentLender,
    organizationId,
  ]);

  const balanceSheetExposureDataQuery = useGetBalanceSheetExposureDataQuery({
    skip: flags.isSlsAgentLender,
    variables: { organizationId },
    fetchPolicy: 'cache-and-network',
  });

  const balanceSheetImpactCalcs = balanceSheetExposureDataQuery.data ?? null;

  return {
    hasBorrowingBase,
    computedRows,
    totalsRow,
    balanceSheetImpactCalcs,
    attributeValues,
    attributeFiltersOptions: [],
    error:
      facilitiesTableQuery.error ||
      balanceSheetExposureDataQuery.error ||
      checkHasBorrowingBaseQuery.error,
    isAnyLoading:
      facilitiesTableQuery.loading ||
      !facilitiesTableQuery.data ||
      balanceSheetExposureDataQuery.loading ||
      !balanceSheetExposureDataQuery.data ||
      checkHasBorrowingBaseQuery.loading ||
      !checkHasBorrowingBaseQuery.data,
    isHasBorrowingBaseLoading:
      checkHasBorrowingBaseQuery.loading || !checkHasBorrowingBaseQuery.data,
    isBalanceSheetExposureLoading:
      balanceSheetExposureDataQuery.loading ||
      !balanceSheetExposureDataQuery.data,
    isFacilitiesTableDataLoading:
      facilitiesTableQuery.loading || !facilitiesTableQuery.data,
  };
};
